import { type FC, useId } from "react";

import { assert } from "./utils";

function capitalize(val: string): string {
  if (val.length == 0)
    return val;
  if (val.length == 1)
    return val.toUpperCase();
  return val.slice(0, 1).toUpperCase() + val.slice(1);
}

const removeUnchangedOnChanged = (ev: Event | React.ChangeEvent<HTMLInputElement|HTMLTextAreaElement>) => {
  const el = ev.target;
  assert(el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement);
  const defaultValue:string = el.dataset["defaultvalue"] || "";
  let value;
  if (el instanceof HTMLInputElement && el.type == "checkbox") {
    value = el.checked ? "true" : "false";
  } else {
    value = el.value;
  }
  const changed = value != defaultValue;
  el.classList.toggle("input-unchanged", !changed);
  return true;
};

type InputRowProps = {
  name: string,
  style?: React.CSSProperties,
  inputRef?: React.Ref<HTMLElement> | undefined,
  testId?: string,
} & ({
  defaultValue: string | null | undefined,
  textarea: true,
  inputRef?: React.Ref<HTMLTextAreaElement> | undefined,
  type?: undefined,
  value?: undefined,
} | {
  defaultValue: string | null | undefined,
  textarea?: false,
  inputRef?: React.Ref<HTMLInputElement> | undefined,
  type?: undefined | "color" | "date" | "datetime-local" | "email" | "month" | "number" | "password" | "tel" | "text" | "time" | "url" | "week",
  value?: undefined,
} | {
  type: "checkbox",
  textarea?: false,
  inputRef?: React.Ref<HTMLInputElement> | undefined,
  defaultChecked: boolean,
  value: string,
}) & ({
  hasLabel?: true,
  label?: string
  inline?: boolean,
} | {
  hasLabel: false,
  label?: undefined,
  inline?: false,
});

export const InputRow:FC<InputRowProps> = (props) => {
  const htmlId = useId();
  const inline = props.inline === undefined ? props.type === "checkbox" : props.inline;
  const hasLabel = props.hasLabel === undefined ? true : props.hasLabel;
  const style = props.style ?? {};
  if (props.textarea && style["resize"] == null) {
    style["resize"] = "vertical";
  }
  const commonAttrs = {
    id: htmlId,
    name: props.name,
    style: props.style,
    className: "input-unchanged",
    "data-defaultvalue": props.type == "checkbox" ? props.defaultChecked : (props.defaultValue || ""),
    onChange: removeUnchangedOnChanged,
    ...(props.testId != null ? {"data-testid": props.testId} : {}),
    // ref doesnt show up here because type inference is hard for typescript
  };
  let inputEl;
  if (props.type === "checkbox") {
    const attrs = {
      type: props.type,
      defaultChecked: props.defaultChecked,
      value: props.value,
      ref: props.inputRef,
      ...commonAttrs,
    };
    inputEl = <input {...attrs} />;
  } else {
    const defaultValue = props.defaultValue || "";
    if (props.textarea) {
      const attrs = {
        defaultValue,
        ref: props.inputRef,
        ...commonAttrs,
      };
      inputEl = <textarea {...attrs} />;
    } else {
      const attrs = {
        defaultValue,
        type: props.type,
        ref: props.inputRef,
        ...commonAttrs,
      };
      inputEl = <input {...attrs} />;
    }
  }
  // const res:React.ReactNode[] = [];
  let labelEl:React.ReactNode | null = null;
  if (hasLabel)
    labelEl = <label htmlFor={htmlId}>{props.label == null ? `${capitalize(props.name)}:` : props.label}</label>;
  const res = <>
    {labelEl}
    {inputEl}
  </>
  if (inline) {
    return <span>{res}</span>;
  } else {
    return res;
  }
}

export const AttrRow:FC<{name: string, defaultValue: string, inputRef?: React.Ref<HTMLInputElement> | undefined, valueSuggestions?: string[]}> = ({name, defaultValue, inputRef, valueSuggestions}) => {
  const listId = useId();
  const hasSugg = valueSuggestions != null && valueSuggestions.length > 0;
  return <tr>
    <td>{name}</td>
    <td>
      {hasSugg && <datalist id={listId}>{valueSuggestions!.map(v => <option key={v} value={v} />)}</datalist>}
      <input
        name={`attr:${name}`}
        defaultValue={defaultValue}
        style={{width: "100%"}}
        className="input-unchanged"
        data-defaultvalue={defaultValue}
        onChange={removeUnchangedOnChanged}
        ref={inputRef}
        list={hasSugg ? listId : undefined}
      />
    </td>
  </tr>;
};
